// src/pages/admin/Organizations.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Input, Select, Table, Modal, Spin, Dropdown } from "antd";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import {
  useGetOrganizationsQuery,
  useDeleteOrganizationMutation,
} from "@/store/api/apiSlice";
import {
  SearchOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { LuFilter, LuX } from "react-icons/lu";
import { ACTIONS_DROPDOWN_PROPS } from "@/constants/actionsDropdown";
import { buildConditionalPagination } from "@/utils/pagination";
import { OrganizationsIcon } from "@/assets/svgs/icons";
import AddOrganizationModal from "../../../components/Modals/AddOrganizationModal";
import { useGetOrganizationStatsQuery } from "../../../store/api/apiSlice";
import { LayoutGrid, List } from "lucide-react";

const { confirm } = Modal;

const StatCard = ({ value, label }) => (
  <div className="bg-secondary rounded-xl p-3">
    <div className="flex flex-col">
      <div className="text-2xl font-semibold mt-0">
        {value?.toLocaleString()}
      </div>
      <div className="text-gray-500 text-sm">{label}</div>
    </div>
  </div>
);

const Organizations = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [viewType, setViewType] = useState(isMobile ? "grid" : "list");
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    status: undefined,
    sort: undefined,
  });
  const [selectedPeriod, setSelectedPeriod] = useState("lifetime");
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [activeFilters, setActiveFilters] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [brokenLogos, setBrokenLogos] = useState({});

  const formatDateToUnix = (date) => {
    if (!date) return null;
    return Math.floor(date.getTime() / 1000);
  };

  const calculateDateRange = (periodValue) => {
    const today = new Date();
    let startDate = null;
    let endDate = formatDateToUnix(today);

    switch (periodValue) {
      case "7days":
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        startDate = formatDateToUnix(sevenDaysAgo);
        break;
      case "30days":
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        startDate = formatDateToUnix(thirtyDaysAgo);
        break;
      case "year":
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(today.getFullYear() - 1);
        startDate = formatDateToUnix(oneYearAgo);
        break;
      case "lifetime":
        startDate = null;
        break;
    }

    return { startDate, endDate };
  };

  const dateParams = useMemo(() => {
    return calculateDateRange(selectedPeriod);
  }, [selectedPeriod]);

  const queryParams = useMemo(() => {
    const params = {
      search: searchText,
      ...filters,
    };

    return params;
  }, [searchText, selectedPeriod, dateParams, filters]);

  const statsQueryParams = useMemo(() => {
    return {
      start_date: selectedPeriod === "lifetime" ? null : dateParams.startDate,
      end_date: dateParams.endDate,
    };
  }, [selectedPeriod, dateParams]);

  const { data: statsData } = useGetOrganizationStatsQuery(statsQueryParams);

  const [deleteOrganization] = useDeleteOrganizationMutation();

  const {
    data: organizations = { data: [] },
    isLoading,
    isFetching,
    refetch: refetchOrganizations,
  } = useGetOrganizationsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const stats = useMemo(
    () => ({
      totalUsers: statsData?.total_users || 0,
      totalInterpreters: statsData?.total_interpreters || 0,
      totalForms: statsData?.total_forms || 0,
      totalCampaigns: statsData?.total_campaigns || 0,
      totalOrganizations: statsData?.total_organizations || 0,
    }),
    [statsData],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchText(value);
      }, 500),
    [],
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchText("");
  };

  const handleFilterChange = (type, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]: value,
    }));
    setActiveFilters(true);
  };

  const clearFilters = () => {
    setFilters({
      status: undefined,
      sort: undefined,
    });
    setActiveFilters(false);
  };

  const showDeleteConfirm = (record) => {
    confirm({
      title: "Are you sure you want to delete this organisation?",
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      async onOk() {
        try {
          await deleteOrganization(record._id).unwrap();
          refetchOrganizations();
        } catch (error) {
          // Error handling is done in the mutation
        }
      },
    });
  };

  const handleEdit = (record) => {
    setEditingOrganization(record);
    setModalVisible(true);
  };

  const getActionsMenuItems = () => [
    { key: "edit", label: "Edit", icon: <EditOutlined /> },
    {
      key: "delete",
      label: "Delete",
      danger: true,
      icon: <DeleteOutlined />,
    },
  ];

  const handleActionMenuClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    switch (actionKey) {
      case "edit":
        handleEdit(record);
        break;
      case "delete":
        showDeleteConfirm(record);
        break;
      default:
        break;
    }
  };

  const columns = [
    {
      title: "Sr#",
      dataIndex: "srNo",
      key: "srNo",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Organisation Info",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <div className="relative w-10 h-10 rounded-xl overflow-hidden bg-gradient-to-br from-gray-50 to-gray-500 border border-gray-300 flex items-center justify-center">
            {!record.logo && <OrganizationsIcon />}
            {record.logo ? (
              <img
                src={record.logo}
                alt={record.name}
                className="w-full h-full object-contain p-1.5 drop-shadow-sm"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : null}
          </div>
          <div>
            <div className="font-medium text-gray-900">{record.name}</div>
            {/* <div className="text-sm text-gray-500 truncate">
              {record.location?.name}
            </div> */}
          </div>
        </div>
      ),
    },
    {
      title: "Campaign",
      key: "campaign",
      render: (_, record) => <span>{record.campaigns.length || 0}</span>,
    },
    {
      title: "Forms",
      key: "forms",
      render: (_, record) => <span>{record.forms.length || 0}</span>,
    },
    {
      title: "Joined Date",
      dataIndex: "date_created",
      key: "date_created",
      render: (date_created) =>
        date_created ? new Date(date_created).toLocaleDateString() : "N/A",
    },
    {
      title: "",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            {...ACTIONS_DROPDOWN_PROPS}
            menu={{
              items: getActionsMenuItems(record),
              onClick: (event) =>
                handleActionMenuClick(record, event.key, event),
            }}
          >
            <button
              type="button"
              className="actions-dots-btn"
              onClick={(e) => e.stopPropagation()}
              aria-label={`Open actions for ${record?.name || "organisation"}`}
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];

  const handleRowClick = (record) => {
    navigate(`/admin/organisations/${record._id}`);
  };

  const organizationList = organizations?.data || [];
  const tablePagination = buildConditionalPagination(organizationList);

  const renderGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
      {organizationList.map((org, index) => {
        const orgKey = org?._id || `org-${index}`;
        const showLogo = Boolean(org?.logo) && !brokenLogos[orgKey];

        return (
          <div
            key={orgKey}
            className="bg-white rounded-xl p-2 border cursor-pointer"
            onClick={() => handleRowClick(org)}
          >
            <div className="flex justify-between items-start mb-0 w-full">
              <div className="flex gap-3 w-full">
                <div className="relative w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-500 border border-gray-300">
                  {!showLogo && <OrganizationsIcon style={{ fontSize: 28 }} />}
                  {showLogo ? (
                    <img
                      src={org.logo}
                      alt={org?.name}
                      className="w-full h-full object-contain p-3 absolute inset-0 drop-shadow-sm"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        setBrokenLogos((prev) => ({ ...prev, [orgKey]: true }));
                      }}
                    />
                  ) : null}
                </div>
                <div className="w-full">
                  <div className="flex justify-between w-full">
                    <h3 className="font-medium text-sm text-gray-900 mb-0 mt-0">
                      {org?.name}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-400 mt-0">
                    {org?.location?.name || "No location"}
                  </p>
                  <div className="flex justify-between items-end flex-wrap gap-2 mt-2">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-gray-700">
                        <span className="text-gray-500">Campaigns</span>
                        <span className="text-gray-900">
                          {org?.campaigns.length || 0}
                        </span>
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-gray-700">
                        <span className="text-gray-500">Forms</span>
                        <span className="text-gray-900">
                          {org?.forms.length || 0}
                        </span>
                      </span>
                    </div>
                    <div
                      className="flex justify-end"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Dropdown
                        {...ACTIONS_DROPDOWN_PROPS}
                        menu={{
                          items: getActionsMenuItems(org),
                          onClick: (event) =>
                            handleActionMenuClick(org, event.key, event),
                        }}
                      >
                        <button
                          type="button"
                          className="actions-dots-btn"
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Open actions for ${
                            org?.name || "organisation"
                          }`}
                        >
                          <MoreOutlined />
                        </button>
                      </Dropdown>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;

      setIsMobile(mobile);

      if (mobile) {
        setViewType("grid");
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (isLoading && !isFetching) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-0 selectSecondary">
        <h3 className="text-md font-semibold m-0">Overview</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
        <StatCard value={stats.totalUsers} label="Total Users" />
        <StatCard value={stats.totalInterpreters} label="Total Interpreters" />
        <StatCard value={stats.totalForms} label="Total Forms" />
        <StatCard value={stats.totalCampaigns} label="Total Campaigns" />
        <StatCard
          value={stats.totalOrganizations}
          label="Total Organisations"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex bg-secondary rounded-xl p-1.5">
          <button
            className={`view-toggle-btn ${
              viewType === "list" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("list")}
          >
            <List size={20} />
          </button>
          <button
            className={`view-toggle-btn ${
              viewType === "grid" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("grid")}
          >
            <LayoutGrid size={20} />
          </button>
        </div>
        <div className="relative flex-1">
          <Input
            placeholder="Search..."
            value={searchInput}
            prefix={<SearchOutlined className="text-lg" />}
            suffix={
              searchInput && (
                <CloseOutlined
                  className="cursor-pointer hover:text-gray-600"
                  onClick={clearSearch}
                />
              )
            }
            onChange={handleSearchChange}
            className="h-[48px] !rounded-2xl bg-white min-w-48"
          />
        </div>
        <Select
          allowClear
          value={filters.sort}
          placeholder="Sort by"
          className="w-[80%] md:w-40 h-[48px] !rounded-2xl"
          onChange={(value) => handleFilterChange("sort", value)}
          options={[
            { value: "newest", label: "Newest" },
            { value: "oldest", label: "Oldest" },
          ]}
        />
        <button
          className={`h-[48px] w-12 flex items-center justify-center rounded-xl ${
            activeFilters ? "bg-primary text-white" : "bg-secondary"
          } border hover:opacity-90`}
          onClick={activeFilters ? clearFilters : undefined}
        >
          {activeFilters ? (
            <LuX size={20} />
          ) : (
            <LuFilter size={20} className="text-red-500" />
          )}
        </button>
      </div>
      <div className="relative">
        {isFetching && (
          <div className="absolute inset-0 bg-white bg-opacity-70 z-10 flex justify-center items-center">
            <Spin size="large" />
          </div>
        )}

        {viewType === "grid" ? (
          renderGridView()
        ) : (
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={organizationList}
            pagination={tablePagination}
            className="forms-table"
            rowClassName="hover:bg-gray-50 cursor-pointer"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
            })}
            scroll={{ x: "100%" }}
          />
        )}
      </div>

      <AddOrganizationModal
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setEditingOrganization(null);
          refetchOrganizations();
        }}
        editData={editingOrganization}
        onSuccess={() => {
          refetchOrganizations();
        }}
      />
    </div>
  );
};

export default Organizations;
